// Type definitions for Farmer Express Offers feature

// Request Type Enum
export enum RequestType {
	SEASONAL = 'SEASONAL',
	ONE_TIME = 'ONE_TIME',
}

// Request interfaces (left panel)
export interface Crop {
	id: string;
	name: string;
	image_url: string;
	country_code?: string;
}

export interface Language {
	id: string;
	name: string;
}

export interface LocalizedTitle {
	en: string;
	ka: string;
}

export interface Service {
	name: string;
}

export interface Request {
	id: string;
	request_id: string;
	farmer_id: string;
	start_date: string;
	end_date: string;
	services_id: string;
	specialist_id: string;
	request_type: string;
	offer_id: string | null;
	offers_count: number;
	days_remaining: number;
	crops: Crop[];
	languages: Language[];
	// Add original_date property
	original_date?: {
		start_date: string;
		end_date: string;
	};
	payment_status?: string;
	// Legacy fields for compatibility
	crop?: Crop;
	service?: Service;
	title?: LocalizedTitle;
	status?: 'active' | 'completed' | 'archived';
	active_for_days?: number;
	active_for_timestamp?: number;
}

// Additional types for detailed offer view
export interface Country {
	id?: string;
	name: any;
	alpha_two: string;
	alpha_three?: string;
}

export interface Farmer {
	id: string;
	user_id: string;
	first_name: string;
	last_name: string;
	full_name: string;
	profile_image_url: string;
	country?: Country;
}

export interface Location {
	address: string;
	latitude: number;
	longitude: number;
}

export interface FarmDetails {
	area: number;
	unit: string;
	location: Location;
}

export interface Attachment {
	id: string;
	name: string;
	url: string;
}

export interface Duration {
	months: number;
	days: number;
}

// For detailed offer view - this matches the specialist version
export interface SeasonalOneTimeOffer {
	id: string;
	crop: Crop;
	farmer: Farmer;
	sent_date: string;
	farmer_id: string;
	request_type: RequestType;
	duration_days?: string;
	duration_timestamp?: string;
	request_valid_for_days?: string;
	request_valid_for_timestamp?: string;
	farm_details: FarmDetails;
	communication_languages: Language[];
	description: string;
	attachments: string[];
	start_date?: string;
	end_date?: string;
	duration?: Duration;
	is_sent?: boolean;
	is_paid?: boolean;
	request_active_days?: string;
	request_active_timestamp?: string;
	price?: number;
	currency?: string;
	f100_url?: string;
	offer_id?: string;
	channelId?: string;
	request_id?: string;
	specialist_id?: string;
	payment_status?: string;
	original_date?: {
		start_date: string;
		end_date: string;
	};
}

// Offer Details interface
export interface OfferDetails {
	id: string;
	farmer_id: string;
	specialist_id: string;
	request_id: string;
	price: string;
	currency: string;
	is_default_price: boolean;
	is_free: boolean;
	consultation_type: string;
	offer_status: string;
	offer_comment: string;
	created_at: string;
	updated_at: string;
}

// API Response interfaces for request details
export interface RequestDetailResponse {
	success: boolean;
	data: {
		id: string;
		farmer_id: string;
		specialist_id: string;
		services_id: string;
		request_type: string;
		start_date: string;
		end_date: string;
		crop_id: string;
		area: number;
		unit: string;
		location_name: string;
		latitude: number;
		longitude: number;
		communication_language: string[];
		problem_description: string;
		specialist_connection_code: string | null;
		offer_id: string | null;
		payment_status: string;
		request_status: string;
		request_files: any[];
		request_id: string;
		created_at: string;
		updated_at: string;
		original_date?: {
			start_date: string;
			end_date: string;
		};
	};
	error: string;
}

export interface UserDataResponse {
	success: boolean;
	data: {
		id: string;
		user_id: string;
		first_name: string;
		last_name: string;
		profile_image: string;
		country: {
			id: string;
			name: string;
			alpha_two: string;
			alpha_three?: string;
		};
		unicode?: {
			alpha_two: string;
			alpha_three?: string;
		};
	};
	error: string;
}

export interface CropDataResponse {
	success: boolean;
	data: {
		id: string;
		name: string;
		image_url: string;
		country_code: string;
	}[];
	error: string;
}

export interface LanguageDataResponse {
	success: boolean;
	data: {
		id: string;
		name: string;
	}[];
	error: string;
}

export interface OfferDetailsResponse {
	success: boolean;
	data: OfferDetails;
	error: string;
}

// Offer interfaces (right panel)
export interface Specialist {
	id: string;
	full_name: string;
	profile_image_url: string;
	country: Country;
}

export interface Offer {
	id: string;
	specialist: Specialist;
	service_delivery_days: number;
	price: number;
	currency?: string;
	status: string;
	fieldbook_url?: string;
	f100_url?: string;

	reference_id?: string;
	payment_status?: 'paid' | 'unpaid' | 'refunded';
	is_paid?: boolean;
	is_free?: boolean;
	active_for_days?: number;
	actions_available?: string[];
	// Add start_date and end_date properties
	start_date?: string;
	end_date?: string;
	// Add original_date property
	original_date?: {
		start_date: string;
		end_date: string;
	};
	// Add comment-related fields
	offer_comment?: string;
	farmer_seen_offer_comment?: boolean;
	comment_count?: number;
}

export interface RequestWithOffers {
	request_id: string;
	offers: Offer[];
}

export interface FilterParams {
	status: string[];
}

export interface CombinedResponse {
	requests: Request[];
	offers_by_request: {
		[key: string]: Offer[];
	};
	total_requests?: number;
	total_offers?: number;
}

// API Response interfaces
export interface ApiResponse<T> {
	success: boolean;
	data: T;
	error?: string;
}

// Store state interfaces
export interface FarmerSeasonalOneTimeOffersState {
	// Request state
	requests: Request[];
	isRequestsLoading: boolean;
	requestsError: string | null;
	requestsFilters: FilterParams;

	// Selected request and its offers
	selectedRequestId: string | null;
	offers: Offer[];
	isOffersLoading: boolean;
	offersError: string | null;

	// Selected offer details (for detail page)
	selectedOffer: SeasonalOneTimeOffer | null;
	isOfferDetailLoading: boolean;
	offerDetailError: string | null;

	// Actions
	setRequests: (requests: Request[]) => void;
	setIsRequestsLoading: (isLoading: boolean) => void;
	setRequestsError: (error: string | null) => void;

	setSelectedRequestId: (requestId: string | null) => void;
	setOffers: (offers: Offer[]) => void;
	setIsOffersLoading: (isLoading: boolean) => void;
	setOffersError: (error: string | null) => void;

	// Selected offer detail actions
	setSelectedOffer: (offer: SeasonalOneTimeOffer | null) => void;
	setIsOfferDetailLoading: (isLoading: boolean) => void;
	setOfferDetailError: (error: string | null) => void;

	// Reset state
	resetState: () => void;
}
